/**
 * RL-908 procedure consent templates (staff side): the wording patients read
 * and sign in the app, per key and revision, in English and Bahasa Melayu.
 * Shapes follow ommri-care-api `dto/consent.dto.ts` (ConsentTemplateDto) and
 * `consents.examples.ts` (TEMPLATE_EXAMPLE, DRAFT_TEMPLATE_EXAMPLE).
 */
export const CONSENT_TEMPLATE_STATUSES = [
  'draft',
  'approved',
  'retired',
] as const;
export type ConsentTemplateStatus = (typeof CONSENT_TEMPLATE_STATUSES)[number];

/**
 * Consultation procedures that need written consent (API
 * `CONSENT_PROCEDURE_CODES`: catalogue procedures with consentRequired), with
 * the catalogue's names.
 */
export const CONSENT_PROCEDURES = [
  { code: 'suturing_5cm', name: 'Suturing (up to 5 cm)' },
  { code: 'iud_insertion', name: 'IUD insertion' },
] as const;

export const CONSENT_TEMPLATE_PERMISSIONS = {
  manage: 'consent_templates:manage',
  approve: 'consent_templates:approve',
} as const;

/** API input limits (class-validator on the DTOs). */
export const CONSENT_TEMPLATE_LIMITS = {
  titleMax: 160,
  textMax: 4000,
  keyPattern: /^[a-z][a-z0-9_]{1,59}$/,
} as const;

export interface LocalizedText {
  en: string;
  /** Bahasa Melayu. */
  ms: string;
}

export interface StaffRef {
  id: string;
  fullName: string;
}

/** The wording sections, in the order the app shows them. */
export const WORDING_SECTIONS = [
  'whatHappens',
  'risks',
  'alternatives',
] as const;
export type WordingSection = (typeof WORDING_SECTIONS)[number];

export interface ConsentWording {
  title: LocalizedText;
  whatHappens: LocalizedText;
  risks: LocalizedText;
  alternatives: LocalizedText;
}

export interface ConsentTemplate extends ConsentWording {
  id: string;
  /** Stable slug shared by every revision. */
  key: string;
  revision: number;
  procedureCode: string;
  contentHash: string;
  status: ConsentTemplateStatus;
  service: { id: string; name: string } | null;
  /** Edit lock: send it back to edit, approve or retire. */
  version: number;
  createdAt: string;
  createdBy: StaffRef;
  updatedAt: string;
  approvedAt: string | null;
  approvedBy: StaffRef | null;
  retiredAt: string | null;
  retiredBy: StaffRef | null;
  /** Consent forms signed against this revision. */
  signedCount: number;
}

export interface ConsentTemplateFilters {
  status: ConsentTemplateStatus | '';
}

/** POST /consent-templates. */
export interface CreateConsentTemplateInput extends ConsentWording {
  key: string;
  procedureCode: string;
  serviceId: string | null;
}

/** PATCH /consent-templates/{id} (fields not sent stay). */
export type UpdateConsentTemplateInput = Partial<
  Omit<CreateConsentTemplateInput, 'key'>
> & { version: number };

/** One key with all its revisions (newest first), as the list shows it. */
export interface ConsentTemplateGroup {
  key: string;
  revisions: ConsentTemplate[];
  latest: ConsentTemplate;
  approved: ConsentTemplate | null;
  draft: ConsentTemplate | null;
}
